import React, { useEffect, useState } from "react";
import { Button, Input, Upload, Tag, Spin, message, Typography, Avatar } from "antd";
import { ArrowLeftOutlined, PaperClipOutlined, SendOutlined, UserOutlined, CustomerServiceOutlined } from "@ant-design/icons";
import { getTicketById, addReply } from "../../services/supportService";

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_COLOR = {
  OPEN: "blue",
  IN_PROGRESS: "orange",
  RESOLVED: "green",
  CLOSED: "default",
};

const CATEGORY_LABEL = {
  BOOKING: "Booking",
  POS: "POS",
  ACCOUNTS: "Accounts",
  CHANNEL_MANAGER: "Channel Manager",
  BOOKING_ENGINE: "Booking Engine",
  PAYMENT_GATEWAY: "Payment Gateway",
  OTHERS: "Others",
};

export default function TicketDetail({ ticketId, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  const fetchTicket = async () => {
    try {
      const res = await getTicketById(ticketId);
      setTicket(res.data);
    } catch {
      message.error("Failed to load ticket");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const res = await addReply(ticketId, { message: replyText, attachments });
      setTicket(res.data);
      setReplyText("");
      setAttachments([]);
    } catch {
      message.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = ({ file }) => {
    const reader = new FileReader();
    reader.onload = (e) => setAttachments((prev) => [...prev, e.target.result]);
    reader.readAsDataURL(file);
    return false;
  };

  if (loading) return <div className="flex justify-center p-8"><Spin /></div>;
  if (!ticket) return null;

  const formatDate = (d) => d ? new Date(d).toLocaleString() : "—";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-gray-800 mb-3 text-sm">
          <ArrowLeftOutlined /> Back to tickets
        </button>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs text-gray-400 mb-1">{ticket.ticketNumber}</div>
            <div className="font-semibold text-gray-800 text-sm leading-snug">{ticket.description?.slice(0,100)}</div>
          </div>
          <Tag color={STATUS_COLOR[ticket.status]}>{ticket.status}</Tag>
        </div>
        <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
          <span>Category: <b>{CATEGORY_LABEL[ticket.category]}</b></span>
          <span>·</span>
          <span>Created: {formatDate(ticket.createdAt)}</span>
          {ticket.resolvedAt && (
            <>
              <span>·</span>
              <span>Resolved: {formatDate(ticket.resolvedAt)}</span>
            </>
          )}
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Original description */}
        <div className="flex gap-3">
          <Avatar size={32} icon={<UserOutlined />} className="bg-blue-500 flex-shrink-0" />
          <div className="bg-blue-50 rounded-lg p-3 text-sm flex-1">
            <div className="font-medium text-blue-700 mb-1">You</div>
            <div className="text-gray-700 whitespace-pre-wrap">{ticket.description}</div>
            {ticket.attachments?.length > 0 && (
              <div className="mt-2 space-y-1">
                {ticket.attachments.map((a, i) => (
                  <a key={i} href={a} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 text-xs">
                    <PaperClipOutlined /> Attachment {i + 1}
                  </a>
                ))}
              </div>
            )}
            <div className="text-gray-400 text-xs mt-2">{formatDate(ticket.createdAt)}</div>
          </div>
        </div>

        {/* Replies */}
        {ticket.replies?.map((reply) => {
          const isSupport = reply.repliedByType === "SUPPORT";
          return (
            <div key={reply.id} className={`flex gap-3 ${isSupport ? "flex-row-reverse" : ""}`}>
              <Avatar
                size={32}
                icon={isSupport ? <CustomerServiceOutlined /> : <UserOutlined />}
                className={`flex-shrink-0 ${isSupport ? "bg-green-500" : "bg-blue-500"}`}
              />
              <div className={`rounded-lg p-3 text-sm flex-1 ${isSupport ? "bg-green-50" : "bg-blue-50"}`}>
                <div className={`font-medium mb-1 ${isSupport ? "text-green-700" : "text-blue-700"}`}>
                  {isSupport ? reply.repliedByName || "Support Team" : "You"}
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">{reply.message}</div>
                {reply.attachments?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {reply.attachments.map((a, i) => (
                      <a key={i} href={a} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 text-xs">
                        <PaperClipOutlined /> Attachment {i + 1}
                      </a>
                    ))}
                  </div>
                )}
                <div className="text-gray-400 text-xs mt-2">{formatDate(reply.repliedAt)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      {ticket.status !== "CLOSED" && (
        <div className="p-4 border-t border-gray-200 space-y-2">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {attachments.map((_, i) => (
                <Tag key={i} closable onClose={() => setAttachments((p) => p.filter((__, j) => j !== i))}>
                  File {i + 1}
                </Tag>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <TextArea
              rows={2}
              placeholder="Type your reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.ctrlKey) handleSendReply();
              }}
              className="flex-1"
            />
            <div className="flex flex-col gap-1">
              <Upload beforeUpload={handleFileChange} showUploadList={false}>
                <Button size="small" icon={<PaperClipOutlined />} />
              </Upload>
              <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
                loading={sending}
                onClick={handleSendReply}
                disabled={!replyText.trim()}
              />
            </div>
          </div>
          <div className="text-gray-400 text-xs">Ctrl+Enter to send</div>
        </div>
      )}
    </div>
  );
}
